import type { CSSProperties, ReactElement } from 'react'
import bannerPinRaw from './assets/banner-pin.svg?raw'
import sectionStarRaw from './assets/section-star.svg?raw'
import sectionPinRaw from './assets/section-pin.svg?raw'

/**
 * Real Figma-exported SVG icons for `@metro/suggester`.
 *
 * These are NOT hand-drawn — `assets/*.svg` are exported 1:1 from the
 * TASK-318 Figma file (`iMUqDtfvz9rQC2Jy0H9arH`):
 * - `banner-pin.svg`  — LocationBanner pin (node 6304:15877), orange `#F54900`.
 * - `section-star.svg` — Popular section-header star (node 6346:15887).
 * - `section-pin.svg`  — Nearby section-header pin (node 6301:15887).
 *
 * These three are the ONLY exportable vector icons in the Figma file —
 * heart / clock / wifi-off / search are SF Symbol glyphs (PUA
 * code-points), not vector nodes, so they live as inline SVGs in
 * `icons.tsx` (the agreed cross-platform source of truth).
 *
 * The markup is inlined via `?raw` + `dangerouslySetInnerHTML` (not an
 * `<img>`) so the star / pin keep `fill="currentColor"` and theme with
 * the section header's `textSecondary` token. The banner pin is a fixed
 * orange and themes nowhere.
 *
 * Inlining trusted, build-time-bundled SVG strings is safe — there is
 * no user input in the markup.
 */

interface FigmaIconProps {
  /** Square box size in px the icon is constrained to. */
  readonly size: number
}

function rawIcon(raw: string, size: number): ReactElement {
  const style: CSSProperties = {
    display: 'inline-flex',
    width: size,
    height: size,
  }
  // `raw` is a build-time-bundled SVG string exported from Figma — a
  // trusted constant, no user input — so inlining it via
  // `dangerouslySetInnerHTML` is safe and preserves `currentColor`.
  return <span aria-hidden style={style} dangerouslySetInnerHTML={{ __html: raw }} />
}

/** LocationBanner pin — orange `#F54900` outline marker (Figma 6304:15877). */
export function BannerPinIcon({ size = 20 }: Partial<FigmaIconProps>): ReactElement {
  return rawIcon(bannerPinRaw, size)
}

/** Popular section-header star — `currentColor` (Figma 6346:15887). */
export function SectionStarIcon({ size = 16 }: Partial<FigmaIconProps>): ReactElement {
  return rawIcon(sectionStarRaw, size)
}

/** Nearby section-header pin — `currentColor` (Figma 6301:15887). */
export function SectionPinIcon({ size = 18 }: Partial<FigmaIconProps>): ReactElement {
  return rawIcon(sectionPinRaw, size)
}
